import React, { useEffect, useState } from 'react';
import { X, History as HistoryIcon, User, Globe, GitCommit } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import auditLogService from '../../services/auditLogService';
import './HistoryDialog.css';

const HistoryDialog = ({ isOpen, onClose, title, appLabel, model, objectId }) => {
  const { t, i18n } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [logs, setLogs] = useState([]);
  const [page, setPage] = useState(1);
  const [next, setNext] = useState(null);
  const [previous, setPrevious] = useState(null);

  useEffect(() => {
    if (!isOpen) return;

    const fetchLogs = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await auditLogService.getObjectHistory({
          appLabel,
          model,
          objectId,
          page: 1,
          pageSize: 20,
        });

        const results = Array.isArray(data?.results) ? data.results : Array.isArray(data) ? data : [];
        setLogs(results);
        setNext(data?.next || null);
        setPrevious(data?.previous || null);
        setPage(1);
      } catch (e) {
        setError(e);
        setLogs([]);
        setNext(null);
        setPrevious(null);
      } finally {
        setLoading(false);
      }
    };

    fetchLogs();
  }, [isOpen, appLabel, model, objectId]);

  if (!isOpen) return null;

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const loadPage = async (newPage) => {
    setLoading(true);
    setError(null);
    try {
      const data = await auditLogService.getObjectHistory({
        appLabel,
        model,
        objectId,
        page: newPage,
        pageSize: 20,
      });

      const results = Array.isArray(data?.results) ? data.results : Array.isArray(data) ? data : [];
      setLogs(results);
      setNext(data?.next || null);
      setPrevious(data?.previous || null);
      setPage(newPage);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  };

  const formatTimestamp = (ts) => {
    try {
      return new Date(ts).toLocaleString(i18n.language);
    } catch {
      return ts;
    }
  };

  const renderChanges = (log) => {
    const actionDescription = log.action_description;
    
    if (!actionDescription || actionDescription.length === 0) {
      return null;
    }

    return (
      <div className="history-changes-list">
        {actionDescription.map((change, idx) => (
          <div key={idx} className="history-change-item">
            <span className="history-change-field">{change.field}:</span>
            {change.type === 'update' && (
              <span className="history-change-values">
                <span className="history-old-value">{change.old_value}</span>
                <span className="history-arrow">→</span>
                <span className="history-new-value">{change.new_value}</span>
              </span>
            )}
            {change.type === 'create' && (
              <span className="history-change-values">
                <span className="history-new-value">{change.new_value}</span>
              </span>
            )}
            {change.type === 'delete' && (
              <span className="history-change-values">
                <span className="history-old-value history-deleted">{change.old_value}</span>
              </span>
            )}
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="dialog-overlay" onClick={handleOverlayClick}>
      <div className="dialog-container view-dialog history-dialog">
        <div className="dialog-header">
          <div className="dialog-title">
            <HistoryIcon size={24} />
            <h2>{title || t('history.title')}</h2>
          </div>
          <button className="dialog-close" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <div className="dialog-content history-content">
          {loading && (
            <div className="history-loading">{t('common.loading')}</div>
          )}

          {!loading && error && (
            <div className="history-error">{t('history.error')}</div>
          )}

          {!loading && !error && logs.length === 0 && (
            <div className="history-empty">{t('history.empty')}</div>
          )}

          {!loading && !error && logs.length > 0 && (
            <div className="history-list">
              {logs.map((log) => (
                <div key={log.id} className="history-item">
                  <div className="history-item-header">
                    <div className="history-action">
                      <GitCommit size={16} />
                      <span className="history-action-text">{log.action}</span>
                    </div>
                    <div className="history-meta">
                      <span className="history-timestamp">{formatTimestamp(log.timestamp)}</span>
                    </div>
                  </div>

                  <div className="history-item-sub">
                    <div className="history-actor">
                      <User size={14} />
                      <span>{log.actor_name || '-'}</span>
                    </div>
                  </div>

                  {renderChanges(log)}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="dialog-actions history-actions">
          <div className="history-pagination">
            <button
              type="button"
              className="btn-secondary"
              onClick={() => loadPage(Math.max(1, page - 1))}
              disabled={!previous || loading || page <= 1}
            >
              {t('common.previous')}
            </button>
            <button
              type="button"
              className="btn-secondary"
              onClick={() => loadPage(page + 1)}
              disabled={!next || loading}
            >
              {t('common.next')}
            </button>
          </div>

          <button type="button" className="btn-primary" onClick={onClose}>
            {t('common.close')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default HistoryDialog;
